import { useMemo } from 'react';

import type { RelevancyMosaicDesign } from '../types';
import { RelevancyCardFormat } from '../types';
import { mosaicAdaptiveFormat } from '../utils';

import { useWindowSize } from '@hooks/useWindowSize';

export type useCardFormatParams = {
  design: RelevancyMosaicDesign;
  isPortrait: boolean;
  isSquare: boolean;
  isOver: boolean;
  cardsSpan: URLSearchParams;
  cardsNumber: number;
  currentCardIndex: number;
  wideCardIndex: number;
  wideCardPeriod: number;
};

export const useCardFormat = ({
  design,
  isPortrait,
  isSquare,
  isOver,
  cardsSpan,
  cardsNumber,
  currentCardIndex,
  wideCardIndex,
  wideCardPeriod,
}: useCardFormatParams) => {
  const { isMobile } = useWindowSize();

  const cardFormat: RelevancyCardFormat = useMemo(() => {
    if (design === 'mosaic-adaptive') {
      if (isMobile) return RelevancyCardFormat.WIDE;

      return mosaicAdaptiveFormat(cardsNumber, currentCardIndex) ?? RelevancyCardFormat.SQUARE_OVER;
    }

    if (isPortrait && design === 'default' && isMobile) return RelevancyCardFormat.WIDE;
    if (design === 'mosaic-single') return RelevancyCardFormat.WIDE;
    if (isPortrait) return RelevancyCardFormat.PORTRAIT;
    // mixin if the design is mosaic square
    if (isSquare && !isMobile && design === 'mosaic-square' && !cardsSpan.has(`${currentCardIndex}`))
      return RelevancyCardFormat.SQUARE_UNDER;
    if (isSquare && !isOver && !isMobile) return RelevancyCardFormat.SQUARE_UNDER;
    if (isSquare && isOver && !isMobile) return RelevancyCardFormat.SQUARE_OVER;

    if (isMobile || currentCardIndex % wideCardPeriod === 0 || currentCardIndex % wideCardPeriod === wideCardIndex)
      return RelevancyCardFormat.WIDE;

    if (isOver && currentCardIndex % wideCardPeriod !== 0 && currentCardIndex % wideCardPeriod !== wideCardIndex)
      return RelevancyCardFormat.PORTRAIT;

    return RelevancyCardFormat.SQUARE;
  }, [
    isPortrait,
    isSquare,
    isMobile,
    currentCardIndex,
    wideCardPeriod,
    wideCardIndex,
    isOver,
    cardsSpan,
    design,
    cardsNumber,
  ]);

  return { cardFormat };
};
